import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { Badge, Button } from '@/components/atoms';
import { categoriesApi } from '@/services/articles';
import {
  intelligenceService,
  type QuentesConfig,
  type QuentesRunResult,
} from '@/services/intelligence';

import styles from './ConfigQuentes.module.css';

const EDITORIA_ORDER = [
  'Esportes',
  'Economia',
  'Mundo',
  'Tempo',
  'Tecnologia',
  'Entretenimento',
  'Saúde',
];

export function ConfigQuentes() {
  const qc = useQueryClient();
  const [cfg, setCfg] = useState<QuentesConfig | null>(null);
  const [runResult, setRunResult] = useState<QuentesRunResult | null>(null);
  // "Rodar agora" gera VÁRIOS rascunhos de uma vez — confirma em 2 cliques.
  const [armed, setArmed] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['quentes-config'],
    queryFn: intelligenceService.getQuentesConfig,
  });
  const { data: categories } = useQuery({
    queryKey: ['wp-categories'],
    queryFn: () => categoriesApi.list(),
  });

  useEffect(() => {
    if (data) setCfg(structuredClone(data));
  }, [data]);

  const save = useMutation({
    mutationFn: (c: QuentesConfig) => intelligenceService.saveQuentesConfig(c),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['quentes-config'] }),
  });
  const run = useMutation({
    mutationFn: () => intelligenceService.runQuentes(),
    onSuccess: (r) => { setRunResult(r); setArmed(false); },
    onError: () => setArmed(false),
  });

  if (isLoading || !cfg) {
    return <section className={styles.root}><p className={styles.muted}>A carregar…</p></section>;
  }

  const set = <K extends keyof QuentesConfig>(k: K, v: QuentesConfig[K]) =>
    setCfg({ ...cfg, [k]: v });

  const setEd = (
    ed: string,
    patch: Partial<QuentesConfig['editorias'][string]>,
  ) =>
    setCfg({
      ...cfg,
      editorias: {
        ...cfg.editorias,
        [ed]: { ...cfg.editorias[ed], ...patch },
      },
    });

  const isLive = cfg.publish_status === 'publish';

  return (
    <section className={styles.root}>
      <header className={styles.header}>
        <div>
          <h1 className={styles.title}>Fluxo de Quentes</h1>
          <p className={styles.subtitle}>
            Esteira automática: rankeia tendências, enriquece com PAA e N fontes,
            escreve com IA e publica no WordPress na categoria da editoria. Corre
            sozinha a cada hora dentro da janela ativa.
          </p>
        </div>
        <div className={styles.headerActions}>
          <Button
            variant={armed ? 'primary' : 'secondary'}
            onClick={() => {
              if (!armed) {
                setArmed(true);
                return;
              }
              run.mutate();
            }}
            disabled={run.isPending}
            title="Roda o pipeline de Quentes e gera vários rascunhos das top tendências do momento"
          >
            {run.isPending
              ? 'A rodar…'
              : armed
                ? 'Confirmar — gera vários rascunhos'
                : 'Rodar lote agora'}
          </Button>
          <Button
            variant="primary"
            onClick={() => save.mutate(cfg)}
            disabled={save.isPending}
          >
            {save.isPending ? 'A gravar…' : 'Salvar'}
          </Button>
        </div>
      </header>

      {save.isSuccess && <p className={styles.ok}>Configuração gravada.</p>}
      {run.isError && (
        <p style={{ color: 'var(--sig-crit)', fontSize: 13 }}>
          Falha ao rodar o lote: {(run.error as Error)?.message || String(run.error)}
        </p>
      )}

      {/* Estado / segurança */}
      <div className={styles.card}>
        <h2 className={styles.cardTitle}>Estado</h2>
        <div className={styles.switches}>
          <label className={styles.switch}>
            <input
              type="checkbox"
              checked={cfg.enabled}
              onChange={(e) => set('enabled', e.target.checked)}
            />
            <span>Esteira ativa</span>
          </label>
          <label className={styles.switch}>
            <input
              type="checkbox"
              checked={cfg.kill_switch}
              onChange={(e) => set('kill_switch', e.target.checked)}
            />
            <span>Kill switch (trava tudo)</span>
          </label>
        </div>

        <div className={`${styles.publishRow} ${isLive ? styles.publishLive : ''}`}>
          <div>
            <span className={styles.publishLabel}>Modo de publicação</span>
            <p className={styles.muted}>
              {isLive
                ? 'PUBLICADO — as matérias vão direto ao ar para os leitores.'
                : 'RASCUNHO — as matérias entram no WordPress como rascunho, invisíveis aos leitores. Seguro para testes.'}
            </p>
          </div>
          <div className={styles.segmented}>
            <button
              type="button"
              className={!isLive ? styles.segActive : styles.seg}
              onClick={() => set('publish_status', 'draft')}
            >
              Rascunho
            </button>
            <button
              type="button"
              className={isLive ? styles.segActiveLive : styles.seg}
              onClick={() => {
                if (
                  isLive ||
                  window.confirm(
                    'Mudar para "Publicado"? O Fluxo de Quentes passa a publicar AO VIVO no site, sem revisão.',
                  )
                )
                  set('publish_status', 'publish');
              }}
            >
              Publicado
            </button>
          </div>
        </div>
      </div>

      {/* Parâmetros globais */}
      <div className={styles.card}>
        <h2 className={styles.cardTitle}>Parâmetros</h2>
        <div className={styles.grid}>
          <Field label="Geografia" hint="BR ou BR-SP, BR-RJ…">
            <input
              className={styles.input}
              value={cfg.geo}
              onChange={(e) => set('geo', e.target.value.toUpperCase())}
            />
          </Field>
          <Field label="Janela de tendências (horas)" hint="4, 24, 48, 168">
            <input
              type="number"
              className={styles.input}
              value={cfg.hours}
              onChange={(e) => set('hours', Number(e.target.value))}
            />
          </Field>
          <Field label="Corte de score" hint="Só produz se score ≥ este valor (0-100)">
            <input
              type="number"
              className={styles.input}
              value={cfg.score_cut}
              onChange={(e) => set('score_cut', Number(e.target.value))}
            />
          </Field>
          <Field label="Mínimo de fontes" hint="Guarda anti-boato: exige N veículos">
            <input
              type="number"
              className={styles.input}
              value={cfg.min_sources}
              onChange={(e) => set('min_sources', Number(e.target.value))}
            />
          </Field>
          <Field label="Máx. por ciclo" hint="Teto de matérias por execução">
            <input
              type="number"
              className={styles.input}
              value={cfg.max_per_cycle}
              onChange={(e) => set('max_per_cycle', Number(e.target.value))}
            />
          </Field>
          <Field label="Janela ativa" hint="Hora de início e fim (UTC)">
            <div className={styles.range}>
              <input
                type="number"
                className={styles.input}
                value={cfg.active_start}
                onChange={(e) => set('active_start', Number(e.target.value))}
              />
              <span className={styles.muted}>até</span>
              <input
                type="number"
                className={styles.input}
                value={cfg.active_end}
                onChange={(e) => set('active_end', Number(e.target.value))}
              />
            </div>
          </Field>
        </div>
      </div>

      {/* Editorias */}
      <div className={styles.card}>
        <h2 className={styles.cardTitle}>Editorias</h2>
        <p className={styles.muted}>
          Liga/desliga cada editoria, define o teto diário e mapeia para a
          categoria correspondente no WordPress.
        </p>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Editoria</th>
              <th>Ativa</th>
              <th>Teto/dia</th>
              <th>Categoria no WordPress</th>
            </tr>
          </thead>
          <tbody>
            {EDITORIA_ORDER.filter((ed) => cfg.editorias[ed]).map((ed) => {
              const row = cfg.editorias[ed];
              return (
                <tr key={ed} className={row.enabled ? '' : styles.rowOff}>
                  <td className={styles.edName}>{ed}</td>
                  <td>
                    <input
                      type="checkbox"
                      checked={row.enabled}
                      onChange={(e) => setEd(ed, { enabled: e.target.checked })}
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      className={styles.inputSm}
                      value={row.cap_dia}
                      onChange={(e) => setEd(ed, { cap_dia: Number(e.target.value) })}
                    />
                  </td>
                  <td>
                    <select
                      className={styles.select}
                      value={row.wp_category_id ?? ''}
                      onChange={(e) =>
                        setEd(ed, {
                          wp_category_id: e.target.value ? Number(e.target.value) : null,
                        })
                      }
                    >
                      <option value="">— sem categoria —</option>
                      {(categories ?? []).map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Resultado de "Rodar agora" */}
      {runResult && (
        <div className={styles.card}>
          <h2 className={styles.cardTitle}>Último ciclo</h2>
          {typeof runResult.ranked === 'number' ? (
            <p className={styles.muted}>
              {runResult.ranked} tendências rankeadas · {runResult.picked} escolhidas ·
              modo {runResult.publish_status}
            </p>
          ) : (
            <p className={styles.muted}>
              Ciclo pulado
              {typeof (runResult as { skipped?: unknown }).skipped === 'string'
                ? ` — ${String((runResult as { skipped?: unknown }).skipped)}`
                : ''}
              .
            </p>
          )}
          {(runResult.produced ?? []).map((p) => (
            <div key={p.wp_id} className={styles.resultRow}>
              <Badge tone={p.status === 'publish' ? 'crit' : 'neutral'}>
                {p.status === 'publish' ? 'PUBLICADO' : 'RASCUNHO'}
              </Badge>
              <span className={styles.edName}>{p.editoria}</span>
              <span>{p.tema}</span>
              <span className={styles.muted}>score {p.score} · {p.sources} fontes</span>
              <a href={p.wp_link} target="_blank" rel="noreferrer" className={styles.link}>
                ver no WP #{p.wp_id}
              </a>
            </div>
          ))}
          {(runResult.skipped ?? []).map((s, i) => (
            <div key={i} className={styles.skipRow}>
              <span className={styles.muted}>pulou:</span> {s.tema} — {s.motivo}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className={styles.field}>
      <span className={styles.fieldLabel}>{label}</span>
      {children}
      {hint && <span className={styles.fieldHint}>{hint}</span>}
    </label>
  );
}
